import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { createRoot, Root } from "react-dom/client";
import { WelcomeScreen } from "./WelcomeScreen";
import {
	ActiveDirectoryCredentialState,
	LoginEventsSettingsModel,
	LoginPasswordDialogType
} from "client/Api/Authentication/authentication";
import { ensureNotNil, isNil } from "client/Misc/utils";
import { VerifyPasswordStep } from "./VerifyPasswordStep";
import { ForcedSignOutStep } from "./ForcedSignOutStep";
import { never } from "client_legacy/ndaw/errors";
import { WelcomeWizardSteps } from "./WelcomeWizardSteps";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { TimezoneStep } from "./TimeZoneStep";
import { DetectedTimeZoneResult, detectTimeZone } from "client/Features/WelcomeWizard/detectTimeZone";
import { init as initPopupQueue } from "client/Features/PopupQueue/PopupQueue";
import { initSibiPop } from "../Sibi/sibiPop";
import { initIntercom } from "../Intercom/intercomWidget";
import { initKellyWidget } from "../KellyWidget/KellyWidget";

export type WelcomeWizardProps = {
	settings: LoginEventsSettingsModel;
	onClose(): void;
};

export const WelcomeWizard = (props: WelcomeWizardProps): ReactElement | null => {
	const {
		settings,
		onClose
	} = props;

	const [activeEventIndex, setActiveEventIndex] = useState<number>(0);

	const loginEvents = useMemo(
		(): readonly LoginEvent[] => getLoginEvents(settings),
		[settings]
	);

	const event: LoginEvent | undefined = loginEvents[activeEventIndex];

	const nextHandler = useCallback(
		(): void => {
			const nextEventIndex: number = activeEventIndex + 1;

			if (nextEventIndex >= loginEvents.length) {
				onClose();
			} else {
				setActiveEventIndex(nextEventIndex);
			}
		},
		[
			activeEventIndex,
			loginEvents.length,
			onClose
		]
	);

	useEffect(
		(): void => {
			if (!event) {
				onClose();
			}
		},
		[
			event,
			onClose
		]
	);

	if (!event) {
		return null;
	}

	const { renderer } = event;

	const eventElement: ReactElement = renderer(nextHandler);

	return (
		<WelcomeScreen
			trackingId="welcome-wizard"
		>
			{eventElement}
		</WelcomeScreen>
	);
};
WelcomeWizard.displayName = "WelcomeWizard";

type LoginEvent = {
	renderer(
		onNext: () => void
	): ReactElement;
};

function getLoginEvents(
	settings: LoginEventsSettingsModel
): readonly LoginEvent[] {
	const {
		authenticatedUserSettings,
		hasTimeZoneAutomaticallySet,
		hideWelcomeScreen,
		passwordDialogType,
		timeZone,
		previouslyDetectedTimeZone
	} = settings;

	const events: LoginEvent[] = [];
	let passwordDialogAddedToLoginEvents = false;

	switch (authenticatedUserSettings.activeDirectoryCredentialState) {
		case ActiveDirectoryCredentialState.Valid:
			// The credential state is valid which does not require any action
			break;
		case ActiveDirectoryCredentialState.Unknown:
		case ActiveDirectoryCredentialState.CannotConnect:
			// Where we cannot connect to Active Directory or an error occured during connecting
			// we don't have enough information to perform a specific action, hence we don't do anything at
			// all
			break;
		case ActiveDirectoryCredentialState.Invalid:
			addPasswordStep(passwordDialogType, events);

			passwordDialogAddedToLoginEvents = true;
			break;
		case ActiveDirectoryCredentialState.NonePresent:
			// In case no record of credentials is known by us (e.g. you switch from OAuth2 to WsFed). It should be
			// interpreted very similar to when requestChangePassword is defined.
			addPasswordStep(passwordDialogType, events);

			passwordDialogAddedToLoginEvents = true;
			break;
		case ActiveDirectoryCredentialState.AccountBlocked:
			events.push({
				renderer: () => (
					<ForcedSignOutStep
						reason="blocked"
					/>
				)
			});
			return events;
		case ActiveDirectoryCredentialState.PasswordHasExpired:
			events.push({
				renderer: () => (
					<ForcedSignOutStep
						reason="password-expired"
					/>
				)
			});
			return events;
		default:
			never(authenticatedUserSettings.activeDirectoryCredentialState);
	}

	// If the user is requested to change his password we need to show the change password dialog, however if the office 365 credentials
	// were found invalid this dialog is already added to the events. Therefore we use the passwordDialogAddedToLoginEvents variable to
	// prevent duplication
	if (authenticatedUserSettings.requestChangePassword && !passwordDialogAddedToLoginEvents) {
		addPasswordStep(passwordDialogType, events);
	}

	if (isNil(timeZone) || hasTimeZoneAutomaticallySet) {
		const result: DetectedTimeZoneResult | undefined = detectTimeZone(
			previouslyDetectedTimeZone,
			timeZone,
		);

		if (result) {
			const {
				detectedTimeZone,
				isAnotherTimeZoneAlreadyPersisted
			} = result;

			events.push({
				renderer: (onNext) => (
					<TimezoneStep
						detectedTimezone={detectedTimeZone}
						isAnotherTimeZoneAlreadyPersisted={isAnotherTimeZoneAlreadyPersisted}
						onNext={onNext}
					/>
				)
			});
		}
	}

	if (authenticatedUserSettings.showWelcomeScreen && !hideWelcomeScreen) {
		const {
			isAdmin
		} = userConfiguration();

		events.push({
			renderer: (onNext) => (
				<WelcomeWizardSteps
					initialActiveStep={authenticatedUserSettings.welcomeScreenFormStep}
					isAdmin={isAdmin}
					onNext={onNext}
				/>
			)
		});
	}

	return events;
}

function addPasswordStep(
	passwordDialogType: LoginPasswordDialogType,
	events: LoginEvent[]
): void {
	switch(passwordDialogType){
		case LoginPasswordDialogType.VerifyPassword:
			events.push({
				renderer: (onNext) => (
					<VerifyPasswordStep
						onNext={onNext}
					/>
				)
			});
		break;
		case LoginPasswordDialogType.None:
			// Do nothing
			break;
		default:
			never(passwordDialogType);
	}
};

export function init(settings: LoginEventsSettingsModel): void {
	const element: HTMLElement = ensureNotNil(
		document.getElementById("welcome-wizard-host")
	);

	const root: Root = createRoot(element);

	const closeHandler = () => {
		// It is very likely the first render (useEffect) of WelcomeWizard will "close" it when there is
		// no step to run. In this case react produces a warning when you try to unmount the root
		// (because it is still rendering)
		setTimeout(
			(): void => {
				root.unmount();
				initPopupQueue();
				initSibiPop();
				initIntercom();
				initKellyWidget();
			},
			0
		);
	};

	root.render(
		<WelcomeWizard
			settings={settings}
			onClose={closeHandler}
		/>
	);
}
